import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useEffectOnce } from 'usehooks-ts';
import { useDebouncedCallback } from 'use-debounce';
import { isEmpty, filter } from 'lodash';
import { InferType, object, string } from 'yup';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form, FormikHelpers } from 'formik';
import { FormGroupControl, FormGroupTypeahead } from '@components/Base';
import { InsurancePolicy } from '@models/InsurancePolicy';
import { mergeInitialValues } from '@utils/formik';
import { insurancePoliciesFetch } from './action';
import { selectSearchInsurancePolicyUserPageReducer } from './selector';

const GROUP_PROPS = { className: 'mb-3' };

const formValuesSchema = object().shape({
  search: string().default('').optional(),
  policyNumber: string().default('').optional(),
});

type FormValues = InferType<typeof formValuesSchema>;

const SearchFormFrontlineUser = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { insurancePolicies } = useSelector(selectSearchInsurancePolicyUserPageReducer);
  const initialValues: FormValues = mergeInitialValues(formValuesSchema.getDefault(), Object.fromEntries(searchParams));

  const handleSubmit = useDebouncedCallback((values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    setSubmitting(false);
    setSearchParams(values);
  }, 500);

  useEffectOnce(() => {
    dispatch(insurancePoliciesFetch());
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={formValuesSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, handleChange, submitForm }) => (
        <Form>
          <Row>
            <Col>
              <FormGroupControl
                name="search"
                label={`Insured search`}
                groupProps={GROUP_PROPS}
                placeholder="Search by name, member ID"
                onChange={(e) => {
                  handleChange(e);
                  (async () => {
                    await submitForm();
                  })();
                }}
              />
            </Col>
            <Col>
              <FormGroupTypeahead
                name="policyNumber"
                label={`Policy`}
                options={filter(insurancePolicies, (policy) => !isEmpty(policy.policyNumber)) ?? []}
                groupProps={GROUP_PROPS}
                labelKey={(option) => {
                  let label = (option as InsurancePolicy).policyNumber as string;
                  if (!isEmpty((option as InsurancePolicy).company)) {
                    label += ` - ${(option as InsurancePolicy).company?.name as string}`;
                  }

                  return label;
                }}
                placeholder="Type or select"
                onChange={(selected) => {
                  (async () => {
                    await setFieldValue(
                      'policyNumber',
                      (selected[0] as InsurancePolicy | undefined)?.policyNumber ?? '',
                    );
                    await submitForm();
                  })();
                }}
              />
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export { SearchFormFrontlineUser };
